import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {CategoryType} from '../../types/CategoryType';
import {DateFilterType} from '../../types/DateFilterType';
import {ReportTypes} from '../../types/enums/ReportTypes';
import {SeasonType} from '../../types/SeasonType';
import {loadSummaryFilterFromSession, saveSummaryFilterToSession} from '../../util/sessionStorage/summaryFilterSessionStorageUtil';

export interface SummaryFilterState {
    seasonFilter?: SeasonType;
    reportFilter: ReportTypes[];
    matchDayFilter: number[];
    categoryFilter?: CategoryType;
    dateFilter?: DateFilterType;
    showReportsWithoutIncidents: boolean;
    isInitialized: boolean;
}

export interface SummaryFilterSetCategoryAction {
    categoryFilter?: CategoryType;
    matchDayFilter: number[];
    reportFilter: ReportTypes[];
}

const savedState = loadSummaryFilterFromSession();
const initialState: SummaryFilterState = savedState ? {
    ...savedState,
    isInitialized: false
} : {
    seasonFilter: undefined,
    reportFilter: [],
    matchDayFilter: [],
    categoryFilter: undefined,
    dateFilter: undefined,
    showReportsWithoutIncidents: true,
    isInitialized: false
};

const summaryFilterSlice = createSlice({
    name: 'summaryFilter',
    initialState,
    reducers: {
        seasonSet: (state, action: PayloadAction<SeasonType | undefined>) => {
            if (state?.seasonFilter?.id !== action.payload?.id) {
                state.seasonFilter = action.payload;
                saveSummaryFilterToSession(state);
            }
        },
        categorySet: (state, action: PayloadAction<SummaryFilterSetCategoryAction>) => {
            if (!action.payload?.categoryFilter) {
                state.categoryFilter = undefined;
                state.matchDayFilter = [];
            } else if (state?.categoryFilter?.id !== action.payload?.categoryFilter?.id) {
                state.categoryFilter = action.payload?.categoryFilter;
                state.matchDayFilter = action.payload.matchDayFilter;
                state.reportFilter = action.payload.reportFilter;
                state.dateFilter = undefined;
            } else {
                return;
            }
            saveSummaryFilterToSession(state);
        },
        matchDaysSet: (state, action: PayloadAction<number[]>) => {
            state.matchDayFilter = action.payload;
            state.dateFilter = undefined;
            saveSummaryFilterToSession(state);
        },
        reportsSet: (state, action: PayloadAction<ReportTypes[]>) => {
            state.reportFilter = action.payload;
            saveSummaryFilterToSession(state);
        },
        dateFilterSet: (state, action: PayloadAction<DateFilterType | undefined>) => {
            state.dateFilter = action.payload;
            state.matchDayFilter = [];
            saveSummaryFilterToSession(state);
        },
        showNoIncidentsSet: (state, action: PayloadAction<boolean>) => {
            state.showReportsWithoutIncidents = action.payload;
            saveSummaryFilterToSession(state);
        },
        summaryFilterReset: (state, action: PayloadAction<Partial<SummaryFilterState>>) => {
            return {...state, ...action.payload};
        },
        summaryFilterInitialized: (state, action: PayloadAction<Partial<SummaryFilterState>>) => {
            if (savedState) {
                return {...savedState, isInitialized: true} as SummaryFilterState;
            } else {
                return {...state, ...action.payload, isInitialized: true};
            }
        },
        setInitialized: (state, action: PayloadAction<boolean>) => {
            state.isInitialized = action.payload;
        }
    }
});

export const {
    seasonSet,
    categorySet,
    matchDaysSet,
    reportsSet,
    dateFilterSet,
    showNoIncidentsSet,
    summaryFilterReset,
    summaryFilterInitialized,
    setInitialized
} = summaryFilterSlice.actions;

export default summaryFilterSlice.reducer;
